import { getFieldSetFieldListDataForFieldSet } from '../api/fieldSetFields/mockData'
import {
  getKeyIdentifierFieldNameFromFields,
  readKeyIdentifierValueFromFieldSetData,
} from '~/cynqueModules/fieldSetKeyIdentifier'
import type { TEntityId } from '~/types/models'
import type { TMSService } from '~/types/msService/models'

const mn = '[api/msw/modules/setKeyIdentifierValues.ts]'

/**
 * Put `keyIdentifierValue` on msServices as they leave the mock.
 *
 * WHY THE MOCK DOES THIS AT ALL
 * -----------------------------
 * `*_ms_services.key_identifier_value` is a real column on all three silo
 * tables and is already in the entity JSON the API builds
 * (`entities._company_ms_services_build`), so a client is entitled to expect it
 * on the wire. Nothing server-side writes it yet -- `set_ms_service_attributes`
 * sets only `label` -- but a mock that can never produce a populated value is a
 * mock narrower than the server it stands in for, and this codebase has been
 * bitten by exactly that before (see the versionConflict note in CLAUDE.md).
 * Emitting it here means every mock consumer gets the value, including the
 * pages that do not prime the app-side cache.
 *
 * DERIVED ON READ, NOT ON WRITE
 * -----------------------------
 * A stored column would be the faithful imitation, but it would need the seed
 * rows backfilled and would go stale the moment a fieldSet's flagged field
 * moved. Deriving as the rows are handed out is self-healing: move the flag and
 * the next read is right. The write is in place on the shared list objects, so
 * it also lands in the persisted mock data when `doPersistMockData` is on --
 * harmless, since the next read overwrites it.
 *
 * ⚠ Cheap by construction, but not free: `getFieldSetFieldListDataForFieldSet`
 * filters the whole fieldSetField list. `_fieldNameByFieldSetId` memoises per
 * call so a hundred rows sharing one fieldSet -- 21 of the 55 mock products
 * share `XXFS0002` -- filter it once.
 */
export async function setKeyIdentifierValues(
  msServices: TMSService[]
): Promise<void> {
  const fn = `${mn} ${setKeyIdentifierValues.name} |`
  const fieldNameByFieldSetId = new Map<TEntityId, string | undefined>()

  for (const msService of msServices) {
    const fieldSetId = msService.product?.fieldSet?.id
    if (!fieldSetId || !msService.fieldSetData) {
      continue
    }
    if (!fieldNameByFieldSetId.has(fieldSetId)) {
      const fields = await getFieldSetFieldListDataForFieldSet(fieldSetId)
      fieldNameByFieldSetId.set(
        fieldSetId,
        getKeyIdentifierFieldNameFromFields(fields)
      )
    }
    const value = readKeyIdentifierValueFromFieldSetData(
      msService.fieldSetData,
      fieldNameByFieldSetId.get(fieldSetId)
    )
    if (value !== undefined) {
      console.debug(
        `${fn} ${msService.id}: keyIdentifierValue '${value}'` +
          ` (fieldSet '${fieldSetId}')`
      )
      msService.keyIdentifierValue = value
    }
  }
}
